/**
 * Slider Controls for Blog Posts and Collection List
 * Handles navigation controls for carousel sections
 */
class SliderControls {
  constructor() {
    this.init();
  }

  init() {
    // Find all sections with slider controls (both blog posts and collection list)
    const blogPostsSections = document.querySelectorAll('.blog-posts-slider-controls');
    const collectionListSections = document.querySelectorAll('.collection-list-slider-controls');
    
    
    blogPostsSections.forEach((controls, index) => {
      this.setupBlogPostsControls(controls);
    });
    
    collectionListSections.forEach((controls, index) => {
      this.setupCollectionListControls(controls);
    });
  }

  setupBlogPostsControls(controlsContainer) {
    const blogPostsElement = controlsContainer.closest('.section-spacing').querySelector('blog-posts');
    if (!blogPostsElement) {
      return;
    }

    const prevBtn = controlsContainer.querySelector('.slider-prev-btn');
    const nextBtn = controlsContainer.querySelector('.slider-next-btn');
    const currentSlideSpan = controlsContainer.querySelector('.current-slide');
    const totalSlidesSpan = controlsContainer.querySelector('.total-slides');

    if (!prevBtn || !nextBtn || !currentSlideSpan || !totalSlidesSpan) {
      return;
    }

    // The carousel element is the blog-posts element itself when it has the carousel class
    const carousel = blogPostsElement.classList.contains('blog-post-list--carousel') ? blogPostsElement : null;
    if (!carousel) {
      return;
    }

    this.setupSliderControls(controlsContainer, carousel, '.blog-post-card');
  }

  setupCollectionListControls(controlsContainer) {
    const sectionElement = controlsContainer.closest('.section-spacing');
    
    const carousel = sectionElement.querySelector('scroll-carousel');
    if (!carousel) {
      return;
    }

    const prevBtn = controlsContainer.querySelector('.slider-prev-btn');
    const nextBtn = controlsContainer.querySelector('.slider-next-btn');
    const currentSlideSpan = controlsContainer.querySelector('.current-slide');
    const totalSlidesSpan = controlsContainer.querySelector('.total-slides');


    if (!prevBtn || !nextBtn || !currentSlideSpan || !totalSlidesSpan) {
      return;
    }

    this.setupSliderControls(controlsContainer, carousel, '.collection-card');
  }

  setupSliderControls(controlsContainer, carousel, cardSelector) {
    const prevBtn = controlsContainer.querySelector('.slider-prev-btn');
    const nextBtn = controlsContainer.querySelector('.slider-next-btn');
    const currentSlideSpan = controlsContainer.querySelector('.current-slide');
    const totalSlidesSpan = controlsContainer.querySelector('.total-slides');


    // Calculate total slides based on visible cards
    const updateSlideCount = () => {
      const cards = carousel.querySelectorAll(cardSelector);
      const totalSlides = Math.ceil(cards.length / this.getCardsPerView());
      totalSlidesSpan.textContent = totalSlides.toString().padStart(2, '0');
    };

    // Get number of cards visible per view
    this.getCardsPerView = () => {
      const carouselWidth = carousel.clientWidth;
      const cardWidth = carousel.querySelector(cardSelector)?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(carousel).gap) || 0;
      
      if (cardWidth === 0) return 1;
      
      return Math.floor((carouselWidth + gap) / (cardWidth + gap));
    };

    // Update current slide counter
    const updateCurrentSlide = () => {
      const cards = carousel.querySelectorAll(cardSelector);
      const scrollLeft = carousel.scrollLeft;
      const cardWidth = cards[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(carousel).gap) || 0;
      
      if (cardWidth === 0) return;
      
      const currentIndex = Math.round(scrollLeft / (cardWidth + gap));
      const currentSlide = Math.min(currentIndex + 1, Math.ceil(cards.length / this.getCardsPerView()));
      
      currentSlideSpan.textContent = currentSlide.toString().padStart(2, '0');
      
      // Update button states
      const totalSlides = Math.ceil(cards.length / this.getCardsPerView());
      prevBtn.disabled = currentSlide <= 1;
      nextBtn.disabled = currentSlide >= totalSlides;
    };

    // Navigate to previous slide
    const goToPrevious = () => {
      const cards = carousel.querySelectorAll(cardSelector);
      const cardWidth = cards[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(carousel).gap) || 0;
      const cardsPerView = this.getCardsPerView();
      
      const currentScrollLeft = carousel.scrollLeft;
      const targetScrollLeft = Math.max(0, currentScrollLeft - (cardWidth + gap) * cardsPerView);
      
      
      carousel.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    };

    // Navigate to next slide
    const goToNext = () => {
      const cards = carousel.querySelectorAll(cardSelector);
      const cardWidth = cards[0]?.offsetWidth || 0;
      const gap = parseInt(getComputedStyle(carousel).gap) || 0;
      const cardsPerView = this.getCardsPerView();
      
      const currentScrollLeft = carousel.scrollLeft;
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
      const targetScrollLeft = Math.min(maxScrollLeft, currentScrollLeft + (cardWidth + gap) * cardsPerView);
      
      
      carousel.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    };

    // Add event listeners
    prevBtn.addEventListener('click', goToPrevious);
    nextBtn.addEventListener('click', goToNext);

    // Listen for scroll events to update counter
    carousel.addEventListener('scroll', throttle(updateCurrentSlide, 100));

    // Listen for resize events to recalculate
    window.addEventListener('resize', throttle(() => {
      updateSlideCount();
      updateCurrentSlide();
    }, 250));

    // Initial setup
    updateSlideCount();
    updateCurrentSlide();

    // Update when carousel content changes
    const observer = new MutationObserver(() => {
      updateSlideCount();
      updateCurrentSlide();
    });
    
    observer.observe(carousel, {
      childList: true,
      subtree: true
    });
  }
}

// Throttle function to limit scroll event frequency
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SliderControls();
  });
} else {
  new SliderControls();
}

// Re-initialize when new sections are loaded (for theme editor)
document.addEventListener('shopify:section:load', () => {
  new SliderControls();
});
